/**
 * Presentation helpers.
 *
 * Currency and timezone live on the agency, so nothing formats money or a timestamp without
 * being told which agency it belongs to. `useFormat()` binds that once from the session.
 */

import { useMemo } from "react";
import { formatDate, formatMoney, relativeDays, type PlainDate } from "@rentorng/core";
import { useSession } from "./session";

export interface Formatter {
  money: (minor: number | null | undefined, opts?: { compact?: boolean; signed?: boolean }) => string;
  date: (date: PlainDate | null | undefined, opts?: { long?: boolean }) => string;
  relative: (date: PlainDate | null | undefined, from: PlainDate) => string;
  dateTime: (value: string | Date | null | undefined) => string;
  currency: string;
}

export function useFormat(): Formatter {
  const { org } = useSession();
  const currency = org?.currency ?? "NGN";
  const timezone = org?.timezone ?? "UTC";

  return useMemo<Formatter>(
    () => ({
      currency,
      money: (minor, opts) => (minor === null || minor === undefined ? "—" : formatMoney(Number(minor), currency, opts)),
      date: (date, opts) => (date ? formatDate(date, opts) : "—"),
      relative: (date, from) => (date ? relativeDays(from, date) : "—"),
      dateTime: (value) => {
        if (!value) return "—";
        const d = typeof value === "string" ? new Date(value) : value;
        if (Number.isNaN(d.getTime())) return "—";
        return new Intl.DateTimeFormat("en-GB", {
          timeZone: timezone,
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }).format(d);
      },
    }),
    [currency, timezone],
  );
}

/** Initials for an avatar chip. Two letters, never more — three stops being readable at 24px. */
/**
 * What a money box holds, and what the API is sent.
 *
 * **A `MoneyInput` holds major units** — the number a person says out loud — and **every endpoint
 * takes minor units**, because money is an integer count of them and never a float (ADR-0003).
 * Something has to multiply, and until now every form did it itself with its own copy of these two
 * lines. Two new forms in the developer module did not, and sent ₦25,000,000 as ₦250,000 — a bug
 * that no unit test could see because both halves were individually correct.
 *
 * So there is one copy, here, and forms import it.
 */
export const major = (minorUnits: unknown): string => (minorUnits ? String(Number(minorUnits) / 100) : "");

export function minor(input: string): number | null {
  const amount = Number(String(input).replace(/,/g, ""));
  return Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) : null;
}

/**
 * "1.4 MB". Bytes on a screen are a number nobody converts in their head.
 *
 * Here rather than beside one screen because two screens now list uploaded files — the sale's
 * paperwork and the offer letter's signed copies — and the same file has to read the same size on
 * both. A second copy of this would be the third place in this repository where one number was
 * formatted two ways.
 */
export function fileSize(bytes: number | null | undefined): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function pluralise(count: number, singular: string, plural?: string): string {
  return `${count} ${count === 1 ? singular : (plural ?? singular + "s")}`;
}

export function titleCase(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/** A phone number as a `tel:` href, tolerant of the spaces people type. */
export function telHref(phone: string | null | undefined): string | undefined {
  if (!phone) return undefined;
  const cleaned = phone.replace(/[^\d+]/g, "");
  return cleaned ? `tel:${cleaned}` : undefined;
}

/** A prefilled WhatsApp link — the channel most tenants actually answer. */
export function whatsappHref(phone: string | null | undefined, message?: string): string | undefined {
  if (!phone) return undefined;
  const cleaned = phone.replace(/[^\d]/g, "");
  if (!cleaned) return undefined;
  const text = message ? `?text=${encodeURIComponent(message)}` : "";
  return `https://wa.me/${cleaned}${text}`;
}

/** Today, in the agency's timezone. Used wherever a form needs a sensible default date. */
export function useToday(): PlainDate {
  const { org } = useSession();
  const timezone = org?.timezone ?? "UTC";
  return useMemo(
    () =>
      new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).format(
        new Date(),
      ),
    [timezone],
  );
}
